import { underlyingSymbols } from "#/config/markets";

/**
 * NOTE: This is a copy from the V4-monorepo fee calculator, it could probably not follow
 * all of our standards since it can change anytime on the BE repo.
 */
export interface FeeConfig {
  // Perpetual fee rates (in decimal form)
  PERPETUAL_MAKER_FEE_RATE: number;
  PERPETUAL_TAKER_FEE_RATE: number;

  // Options fee rates (based on asset type)
  OPTIONS_FEE_RATE_MAJOR: number;
  OPTIONS_FEE_RATE_ALT: number;

  // Liquidation fee rates
  LIQUIDATION_FEE_RATE_PERPETUAL_MAJOR: number;
  LIQUIDATION_FEE_RATE_PERPETUAL_ALT: number;
  LIQUIDATION_FEE_RATE_OPTIONS_USDC: number;

  // System parameters
  MAX_FEE_PERCENTAGE: number;
  SYSTEMIC_FEE_RATE: number;

  // Major assets that use the lower fee rate
  MAJOR_ASSETS: Set<string>;
}

/**
 * Fee calculator utility for calculating trading fees
 *
 * Handles fee calculations for:
 * - Maker fees (lower rates for perpetuals)
 * - Taker fees (higher rates for perpetuals)
 * - Liquidation fees (different rates for major vs alt assets, options vs perpetuals)
 * - Systemic risk fees
 *
 * Fees are calibrated based on asset type (major vs alternative)
 * and have maximum caps based on option prices.
 */
export class FeeCalculator {
  private static readonly FEE_CONFIG = {
    // Perpetual fee rates (in decimal form)
    PERPETUAL_MAKER_FEE_RATE: 0, // Previously, 0 bps for maker perpetuals
    PERPETUAL_TAKER_FEE_RATE: 0, // Previously, 8 bps for taker perpetuals

    // Options fee rates (based on asset type)
    OPTIONS_FEE_RATE_MAJOR: 0, // Previously, 4 bps for major assets
    OPTIONS_FEE_RATE_ALT: 0, // Previously, 8 bps for alt assets

    // Liquidation fee rates
    LIQUIDATION_FEE_RATE_PERPETUAL_MAJOR: 0.0004, // 4 bps for major perpetuals
    LIQUIDATION_FEE_RATE_PERPETUAL_ALT: 0.0008, // 8 bps for alt perpetuals
    LIQUIDATION_FEE_RATE_OPTIONS_USDC: 0.0008, // 8 bps for USDC options

    // System parameters
    MAX_FEE_PERCENTAGE: 0.125, // 12.5% cap for option fees
    SYSTEMIC_FEE_RATE: 0.000_025, // 0.0025% systemic risk fee

    // Major assets that use the lower fee rate
    MAJOR_ASSETS: new Set(underlyingSymbols),
  };

  /**
   * Calculates trading fee based on contract type and asset
   */
  public static calculateTradingFee(
    indexPrice: number,
    optionPrice: number | null,
    asset: string,
    isPerp: boolean,
    amount: number,
    isMaker = false,
    config: FeeConfig = FeeCalculator.FEE_CONFIG,
  ): number {
    const isMajorAsset = config.MAJOR_ASSETS.has(asset);

    if (isPerp) {
      // For perpetuals: fee depends on maker/taker status, not asset type
      const feeRate = isMaker
        ? config.PERPETUAL_MAKER_FEE_RATE
        : config.PERPETUAL_TAKER_FEE_RATE;

      const fee = indexPrice * Math.abs(amount) * feeRate;

      return Number(fee.toFixed(6));
    }
    // For options: fee depends on asset type (major/alt), not maker/taker status
    const feeRate = isMajorAsset
      ? config.OPTIONS_FEE_RATE_MAJOR
      : config.OPTIONS_FEE_RATE_ALT;

    const calculatedFee = indexPrice * feeRate * Math.abs(amount);

    // Apply the 12.5% cap on option price when available
    if (optionPrice === null) {
      return Number(calculatedFee.toFixed(6));
    }
    const maxFee = optionPrice * Math.abs(amount) * config.MAX_FEE_PERCENTAGE;
    return Number(Math.min(calculatedFee, maxFee).toFixed(6));
  }

  /**
   * Helper method to calculate maker fee
   */
  public static calculateMakerFee(
    indexPrice: number,
    optionPrice: number | null,
    asset: string,
    isPerp: boolean,
    amount: number,
    config: FeeConfig = FeeCalculator.FEE_CONFIG,
  ): number {
    // Pass isMaker=true to indicate this was a maker fee request
    return this.calculateTradingFee(
      indexPrice,
      optionPrice,
      asset,
      isPerp,
      amount,
      true, // isMaker = true
      config,
    );
  }

  /**
   * Helper method to calculate taker fee
   */
  public static calculateTakerFee(
    indexPrice: number,
    optionPrice: number | null,
    asset: string,
    isPerp: boolean,
    amount: number,
    config: FeeConfig = FeeCalculator.FEE_CONFIG,
  ): number {
    // Pass isMaker=false to indicate this was a taker fee request
    return this.calculateTradingFee(
      indexPrice,
      optionPrice,
      asset,
      isPerp,
      amount,
      false, // isMaker = false
      config,
    );
  }
  /**
   * Calculates liquidation fee using Deribit's model
   */
  public static calculateLiquidationFee(
    indexPrice: number,
    optionPrice: number | null,
    asset: string,
    isPerp: boolean,
    amount: number,
    config: FeeConfig = FeeCalculator.FEE_CONFIG,
  ): number {
    const isMajorAsset = config.MAJOR_ASSETS.has(asset);

    if (isPerp) {
      // For perpetuals:
      // - 15 bps of notional for major assets
      // - 25 bps of notional for alt assets
      // TODO: pull from DB
      const liquidationFeeRate = isMajorAsset
        ? config.LIQUIDATION_FEE_RATE_PERPETUAL_MAJOR
        : config.LIQUIDATION_FEE_RATE_PERPETUAL_ALT;

      return Number(
        (indexPrice * Math.abs(amount) * liquidationFeeRate).toFixed(6),
      );
    }
    // For options:
    // 1. Calculate total fee if liquidated (0.19% × index price × option price × 25% × contracts)
    // 2. Subtract regular trading fee

    if (!optionPrice) {
      throw new Error(
        JSON.stringify({
          type: "VALUE_INVALID",
          value: optionPrice ?? 0,
          variableName: "optionPrice",
          reason: "value must be provided",
        }),
      );
    }

    // For options: calculate fee based on notional (index price × amount)
    const liquidationFeeRate = config.LIQUIDATION_FEE_RATE_OPTIONS_USDC;
    const calculatedFee = indexPrice * liquidationFeeRate * Math.abs(amount);
    const maxFee = optionPrice * Math.abs(amount) * config.MAX_FEE_PERCENTAGE;

    return Number(Math.min(calculatedFee, maxFee).toFixed(6));
  }

  /**
   * Calculates systemic fee based on index price
   */
  public static calculateSystemicFee(
    indexPrice: number,
    isPerp: boolean,
    amount: number,
    config: FeeConfig = FeeCalculator.FEE_CONFIG,
  ): number {
    if (isPerp) {
      // For perpetuals: fee based on notional value
      return Number(
        (indexPrice * Math.abs(amount) * config.SYSTEMIC_FEE_RATE).toFixed(6),
      );
    }

    return Number(
      (indexPrice * Math.abs(amount) * config.SYSTEMIC_FEE_RATE).toFixed(6),
    );
  }
}
